const normalizeDetail = (detail) => {
  if (typeof detail === 'string' && detail.trim()) return detail.trim()

  if (Array.isArray(detail) && detail.length) {
    const joined = detail
      .map((item) => item?.msg || item?.message || (typeof item === 'string' ? item : ''))
      .filter(Boolean)
      .join(', ')

    if (joined.trim()) return joined.trim()
  }

  return ''
}

const DEFAULT_ERROR_MAP = {
  notFound: 'This item is no longer available. Refresh and try again.',
  forbidden: 'You no longer have permission to perform this action.',
  conflict: 'This action could not be completed because the state changed. Refresh and try again.',
}

const STATUS_TEXT_MAP = {
  404: 'notFound',
  403: 'forbidden',
  409: 'conflict',
}

export const getLifecycleStatusMeta = (entityOrDeleted) => {
  const deleted = typeof entityOrDeleted === 'boolean'
    ? entityOrDeleted
    : entityOrDeleted?.is_deleted === true

  return deleted
    ? { label: 'Deleted', className: 'deleted' }
    : { label: 'Active', className: 'active' }
}

export const parseAdminError = (err, fallback, options = {}) => {
  if (typeof err === 'string' && err.trim()) {
    return err.trim()
  }

  const status = Number(err?.response?.status || 0)
  const detail = normalizeDetail(err?.response?.data?.detail)

  const mergedOptions = {
    ...DEFAULT_ERROR_MAP,
    ...options,
  }
  const mappedOptionKey = STATUS_TEXT_MAP[status]

  if (mappedOptionKey) {
    if (mappedOptionKey === 'forbidden') {
      if (options.forbidden) return options.forbidden
      return detail || mergedOptions[mappedOptionKey] || fallback
    }
    return mergedOptions[mappedOptionKey] || detail || fallback
  }

  if (detail) return detail
  if (typeof err?.message === 'string' && err.message.trim()) return err.message.trim()

  return fallback
}

export const parseActionError = (err, fallback, options = {}) => {
  if (typeof err === 'string') {
    const normalized = err.trim().toLowerCase()
    if (normalized === 'conflict' || normalized === '409') {
      return options.conflict || DEFAULT_ERROR_MAP.conflict
    }
    if (normalized === 'forbidden' || normalized === '403') {
      return options.forbidden || DEFAULT_ERROR_MAP.forbidden
    }
    if (normalized === 'not found' || normalized === '404') {
      return options.notFound || DEFAULT_ERROR_MAP.notFound
    }
  }

  return parseAdminError(err, fallback, options)
}

export const toAdminLoadingMessage = (subject = 'results') => `Loading ${subject}...`

export const toAdminSuccessMessage = (subject, verb = 'updated') => {
  const normalizedSubject = String(subject || 'Item').trim() || 'Item'
  const normalizedVerb = String(verb || 'updated').trim() || 'updated'
  return `${normalizedSubject} ${normalizedVerb} successfully.`
}

export const getAdminEmptyStateMessage = (type, context = {}) => {
  if (type === 'organizations') {
    if (context.statusFilter === 'deleted') return 'No deleted organizations found.'
    if (context.statusFilter === 'active') return 'No active organizations found.'
    return 'No organizations found.'
  }

  if (type === 'suborganizations') {
    if (!context.hasOrganization) return 'Select an organization to view sub-organizations.'
    if (context.parentDeleted) return 'No sub-organizations found. Deleted organizations remain read-only until restored.'
    return 'No sub-organizations found for the selected organization.'
  }

  if (type === 'users') {
    return 'No users found for the current selection.'
  }

  return 'No results found.'
}

export const toLifecycleWarningMessage = ({ org, hasSubOrgs = false, hasUsers = false, hasLotteries = false } = {}) => {
  if (org?.is_deleted !== true) return ''

  const cascades = [
    hasSubOrgs ? 'sub-organizations' : '',
    hasUsers ? 'users' : '',
    hasLotteries ? 'lotteries' : '',
  ].filter(Boolean)

  if (!cascades.length) {
    return 'Deleted organizations are read-only until restored.'
  }

  return `Deleted organization is read-only. Related ${cascades.join(', ')} must remain lifecycle-safe until restore.`
}

export const toLifecycleIntegrityMessage = (integrity = {}) => {
  if (!integrity?.hasIssues) return ''
  const warnings = Array.isArray(integrity?.warnings) ? integrity.warnings : []
  return warnings.join(' ')
}

